import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Clock, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_BUSINESS_HOURS, type BusinessHours } from '@/lib/settings-service';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

const TIMEZONES = [
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time' },
];

export default function BusinessHoursSection() {
  const [hours, setHours] = useState<BusinessHours>(DEFAULT_BUSINESS_HOURS);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleDayChange = (
    day: keyof Omit<BusinessHours, 'timezone'>,
    field: 'open' | 'close' | 'closed',
    value: string | boolean
  ) => {
    setHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleTimezoneChange = (timezone: string) => {
    setHours(prev => ({ ...prev, timezone }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    // In a full implementation, this would save to a settings table in the database
    await new Promise(resolve => setTimeout(resolve, 500));
    toast.success('Business hours saved successfully');
    setHasChanges(false);
    setLoading(false);
  };

  const handleReset = () => {
    setHours(DEFAULT_BUSINESS_HOURS);
    setHasChanges(false);
  };

  const formatDayName = (day: string) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Business Hours
        </CardTitle>
        <CardDescription>
          Set operating hours and timezone for your business
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={hours.timezone} onValueChange={handleTimezoneChange}>
              <SelectTrigger className="w-full md:w-64">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hours Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="grid grid-cols-4 gap-4 p-3 bg-muted/50 border-b text-sm font-medium">
              <div>Day</div>
              <div>Open</div>
              <div>Close</div>
              <div className="text-center">Closed</div>
            </div>
            {DAYS.map((day) => {
              const dayHours = hours[day];
              
              return (
                <div
                  key={day}
                  className="grid grid-cols-4 gap-4 p-3 items-center border-b last:border-b-0"
                >
                  <div className="font-medium">{formatDayName(day)}</div>
                  <div>
                    <Input
                      type="time"
                      value={dayHours.open}
                      onChange={(e) => handleDayChange(day, 'open', e.target.value)}
                      disabled={dayHours.closed}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Input
                      type="time"
                      value={dayHours.close}
                      onChange={(e) => handleDayChange(day, 'close', e.target.value)}
                      disabled={dayHours.closed}
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={dayHours.closed}
                      onCheckedChange={(checked) => handleDayChange(day, 'closed', checked)}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 pt-4 border-t">
            <Button onClick={handleSave} disabled={loading || !hasChanges}>
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
            {hasChanges && (
              <Button variant="outline" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
